import {
  Coins,
  Building,
  CheckCircle,
  ChevronDown,
  Crown,
  LogOut,
  UserCircle,
  UserCog,
  Users,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "../../contexts/LanguageContext";
import { cn } from "../../utils/cn";

interface UserProfileMenuProps {
  variant?:
    | "mobile"
    | "desktop"
    | "sidebar"
    | "sidebar-collapsed"
    | "icon-only";
}

const UserProfileMenu: React.FC<UserProfileMenuProps> = ({
  variant = "desktop",
}) => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Fechar com ESC
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape") setIsOpen(false);
      };
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [isOpen]);

  // Obter inicial do nome para avatar
  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleLogout = () => {
    setIsOpen(false);
    logout(false); // Não redirecionar automaticamente, usar navigate do React Router
    navigate("/auth", { replace: true });
  };

  const handleSettingsClick = () => {
    setIsOpen(false);
    navigate("/credits");
  };

  const handleAdminPanelClick = () => {
    setIsOpen(false);
    navigate("/admin");
  };

  const handleAdminCompaniesClick = () => {
    setIsOpen(false);
    navigate("/admin/companies");
  };

  if (!user) return null;

  const userInitials = getInitials(user.name || user.email);

  // ── Variant: sidebar-collapsed ──────────────────────────────────────────
  if (variant === "sidebar-collapsed") {
    return (
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-center w-8 h-8 rounded-full transition-all focus:outline-hidden"
          aria-label={t("Menu do usuário")}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-linear-to-br from-purple-100 via-purple-200 to-purple-300 text-purple-800 font-semibold text-xs ring-2 ring-purple-200 border border-purple-300">
            {userInitials}
          </div>
        </button>
        {isOpen && (
          <div
            ref={menuRef}
            className="absolute bottom-6 left-12 w-64 bg-white/95 backdrop-blur-xl rounded-[1.25rem] shadow-2xl shadow-purple-500/10 border border-purple-200 py-2 z-9999 animate-in fade-in slide-in-from-left-2 duration-200"
            role="menu"
          >
            <div className="px-5 py-3 border-b border-purple-50">
              <p className="text-xs font-black uppercase tracking-widest text-purple-400 mb-1">
                {t("Perfil")}
              </p>
              <p className="text-sm font-black text-gray-900 truncate">
                {user.name || t("Usuário")}
              </p>
              <p className="text-[10px] font-bold text-gray-400 truncate">
                {user.email}
              </p>
            </div>
            <div className="py-1">
              <Link
                to="/profile"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 px-5 py-2.5 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all rounded-lg mx-1"
                role="menuitem"
              >
                <UserCircle className="h-4 w-4 text-purple-500" />
                <span>{t("Meu Perfil")}</span>
              </Link>
              <button
                onClick={handleSettingsClick}
                className="w-full flex items-center gap-3 px-5 py-2.5 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all text-left rounded-lg mx-1"
                role="menuitem"
              >
                <Coins className="h-4 w-4 text-purple-500" />
                <span>{t("Créditos e consumo")}</span>
              </button>
              <div className="border-t border-purple-50 my-1" />
              <Link
                to="/organization"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 px-5 py-2.5 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all rounded-lg mx-1"
                role="menuitem"
              >
                <Building className="h-4 w-4 text-purple-500" />
                <span>{t("Minha Organização")}</span>
              </Link>
              <Link
                to="/organization/clients"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 px-5 py-2.5 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all rounded-lg mx-1"
                role="menuitem"
              >
                <Users className="h-4 w-4 text-purple-500" />
                <span>{t("Empresas Cliente")}</span>
              </Link>
              {(user?.org_role === "SUPER_ADMIN" ||
                user?.org_role === "ADMIN") && (
                <Link
                  to="/organization/users"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-3 px-5 py-2.5 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all rounded-lg mx-1"
                  role="menuitem"
                >
                  <UserCog className="h-4 w-4 text-purple-500" />
                  <span>{t("Usuários")}</span>
                </Link>
              )}
              {user.is_admin && (
                <button
                  onClick={handleAdminPanelClick}
                  className="w-full flex items-center gap-3 px-5 py-2.5 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all text-left rounded-lg mx-1"
                  role="menuitem"
                >
                  <Crown className="h-4 w-4 text-purple-500" />
                  <span>{t("Painel Admin (Auditoria)")}</span>
                </button>
              )}
            </div>
            <div className="border-t border-purple-50 my-1" />
            <div className="py-1">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-5 py-2.5 text-sm font-black text-rose-600 hover:bg-rose-50 transition-all text-left rounded-lg mx-1"
                role="menuitem"
              >
                <LogOut className="h-4 w-4" />
                <span>{t("Encerrar Sessão")}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Variant: sidebar (expanded footer) ───────────────────────────────────
  if (variant === "sidebar") {
    return (
      <div className="relative w-full">
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center gap-2 px-1 py-1 rounded-lg hover:bg-purple-50 transition-colors group focus:outline-hidden"
          aria-label={t("Menu do usuário")}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <div className="relative shrink-0">
            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-linear-to-br from-purple-100 via-purple-200 to-purple-300 text-purple-800 font-semibold text-xs ring-2 ring-purple-200 border border-purple-300">
              {userInitials}
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p className="text-xs font-semibold text-gray-800 group-hover:text-purple-700 truncate">
              {user.name || t("Usuário")}
            </p>
            <p className="text-[10px] text-gray-400 truncate">{user.email}</p>
          </div>
          <ChevronDown
            className={`h-3.5 w-3.5 text-gray-400 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180 text-purple-600" : ""}`}
          />
        </button>
        {isOpen && (
          <div
            ref={menuRef}
            className="absolute bottom-full left-0 right-0 mb-3 w-full bg-white/95 backdrop-blur-xl rounded-[1.25rem] shadow-2xl shadow-purple-500/10 border border-purple-200 py-2 z-9999 animate-in fade-in slide-in-from-bottom-2 duration-200"
            role="menu"
          >
            <div className="px-5 py-3 border-b border-purple-50">
              <p className="text-xs font-black uppercase tracking-widest text-purple-400 mb-1">
                {t("Perfil")}
              </p>
              <p className="text-sm font-black text-gray-900 truncate">
                {user.name || t("Usuário")}
              </p>
              <p className="text-[10px] font-bold text-gray-400 truncate">
                {user.email}
              </p>
            </div>
            <div className="py-1">
              <Link
                to="/profile"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 px-5 py-2.5 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all rounded-lg mx-1"
                role="menuitem"
              >
                <UserCircle className="h-4 w-4 text-purple-500" />
                <span>{t("Meu Perfil")}</span>
              </Link>
              <button
                onClick={handleSettingsClick}
                className="w-full flex items-center gap-3 px-5 py-2.5 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all text-left rounded-lg mx-1"
                role="menuitem"
              >
                <Coins className="h-4 w-4 text-purple-500" />
                <span>{t("Créditos e consumo")}</span>
              </button>
              <div className="border-t border-purple-50 my-1" />
              <Link
                to="/organization"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 px-5 py-2.5 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all rounded-lg mx-1"
                role="menuitem"
              >
                <Building className="h-4 w-4 text-purple-500" />
                <span>{t("Minha Organização")}</span>
              </Link>
              <Link
                to="/organization/clients"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 px-5 py-2.5 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all rounded-lg mx-1"
                role="menuitem"
              >
                <Users className="h-4 w-4 text-purple-500" />
                <span>{t("Empresas Cliente")}</span>
              </Link>
              {(user?.org_role === "SUPER_ADMIN" ||
                user?.org_role === "ADMIN") && (
                <Link
                  to="/organization/users"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-3 px-5 py-2.5 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all rounded-lg mx-1"
                  role="menuitem"
                >
                  <UserCog className="h-4 w-4 text-purple-500" />
                  <span>{t("Usuários")}</span>
                </Link>
              )}
              {user.is_admin && (
                <button
                  onClick={handleAdminCompaniesClick}
                  className="w-full flex items-center gap-3 px-5 py-2.5 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all text-left rounded-lg mx-1"
                  role="menuitem"
                >
                  <Building className="h-4 w-4 text-purple-500" />
                  <span>{t("Organizações Globais")}</span>
                </button>
              )}
              {user.is_admin && (
                <button
                  onClick={handleAdminPanelClick}
                  className="w-full flex items-center gap-3 px-5 py-2.5 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all text-left rounded-lg mx-1"
                  role="menuitem"
                >
                  <Crown className="h-4 w-4 text-purple-500" />
                  <span>{t("Painel Admin (Auditoria)")}</span>
                </button>
              )}
            </div>
            <div className="border-t border-purple-50 my-1" />
            <div className="py-1">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-5 py-2.5 text-sm font-black text-rose-600 hover:bg-rose-50 transition-all text-left rounded-lg mx-1"
                role="menuitem"
              >
                <LogOut className="h-4 w-4" />
                <span>{t("Encerrar Sessão")}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Variant: icon-only (GCP style — avatar circular) ───────────────────────
  if (variant === "icon-only") {
    return (
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-center w-9 h-9 rounded-full focus:outline-hidden group"
          aria-label={t("Menu do usuário")}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-full bg-purple-600 text-white font-semibold text-sm transition-all ${
              isOpen
                ? "ring-2 ring-purple-500 ring-offset-2"
                : "group-hover:ring-2 group-hover:ring-purple-400 group-hover:ring-offset-2"
            }`}
          >
            {userInitials}
          </div>
        </button>
        {isOpen && (
          <div
            ref={menuRef}
            className="absolute right-0 top-full mt-3 w-72 bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl shadow-purple-500/10 border border-purple-200 overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-200 origin-top-right"
            role="menu"
          >
            {/* Header com info do usuário */}
            <div className="px-6 py-5 bg-linear-to-br from-purple-50/50 to-white border-b border-purple-50">
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-purple-600 text-white font-black text-lg shadow-lg shadow-purple-600/20 shrink-0">
                  {userInitials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-black uppercase tracking-widest text-purple-400 mb-0.5">
                    {t("Sessão Ativa")}
                  </p>
                  <p className="text-base font-black text-gray-900 truncate leading-tight">
                    {user.name || t("Usuário")}
                  </p>
                  <p className="text-xs font-bold text-gray-400 truncate mt-0.5">
                    {user.email}
                  </p>
                </div>
              </div>
            </div>
            <div className="py-2 px-1">
              <Link
                to="/profile"
                onClick={() => setIsOpen(false)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setIsOpen(false);
                  navigate("/profile");
                }}
                className="flex items-center gap-3 px-5 py-3 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all rounded-xl"
                role="menuitem"
              >
                <UserCircle className="h-5 w-5 text-purple-500" />
                <span>{t("Meu Perfil")}</span>
              </Link>
              <button
                onClick={handleSettingsClick}
                onContextMenu={(e) => {
                  e.preventDefault();
                  handleSettingsClick();
                }}
                className="w-full flex items-center gap-3 px-5 py-3 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all text-left rounded-xl"
                role="menuitem"
              >
                <Coins className="h-5 w-5 text-purple-500" />
                <span>{t("Créditos e consumo")}</span>
              </button>
              <div className="border-t border-purple-50 my-1 mx-2" />
              <p className="px-5 pt-2 pb-1 text-[10px] font-black uppercase tracking-widest text-purple-400">
                {t("Organização")}
              </p>
              <Link
                to="/organization"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-4 px-5 py-3 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all rounded-xl"
                role="menuitem"
              >
                <Building className="h-5 w-5 text-purple-500" />
                <span>{t("Minha Organização")}</span>
              </Link>
              <Link
                to="/organization/clients"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-4 px-5 py-3 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all rounded-xl"
                role="menuitem"
              >
                <Users className="h-5 w-5 text-purple-500" />
                <span>{t("Empresas Cliente")}</span>
              </Link>
              {(user?.org_role === "SUPER_ADMIN" ||
                user?.org_role === "ADMIN") && (
                <Link
                  to="/organization/users"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-4 px-5 py-3 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all rounded-xl"
                  role="menuitem"
                >
                  <UserCog className="h-5 w-5 text-purple-500" />
                  <span>{t("Usuários")}</span>
                </Link>
              )}
              {user.is_admin && (
                <button
                  onClick={handleAdminCompaniesClick}
                  className="w-full flex items-center gap-3 px-5 py-3 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all text-left rounded-xl"
                  role="menuitem"
                >
                  <Building className="h-5 w-5 text-purple-500" />
                  <span>{t("Organizações Globais")}</span>
                </button>
              )}
              {user.is_admin && (
                <button
                  onClick={handleAdminPanelClick}
                  className="w-full flex items-center gap-3 px-5 py-3 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all text-left rounded-xl"
                  role="menuitem"
                >
                  <Crown className="h-5 w-5 text-purple-500" />
                  <span>{t("Painel Admin (Auditoria)")}</span>
                </button>
              )}
            </div>
            <div className="border-t border-purple-50 mx-2" />
            <div className="py-2 px-1">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-5 py-3 text-sm font-black text-rose-600 hover:bg-rose-50 transition-all text-left rounded-xl"
                role="menuitem"
              >
                <LogOut className="h-5 w-5" />
                <span>{t("Encerrar Sessão")}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Variant: mobile | desktop (default) ──────────────────────────────────
  return (
    <div className="relative">
      {/* Botão do perfil */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "group flex items-center justify-between",
          // Altura padronizada com o ContextSwitcher
          "h-9 sm:h-9 md:h-10",
          "px-2 sm:px-2.5 md:px-3",
          "rounded-lg transition-all duration-200",
          "border border-purple-300 hover:border-purple-300",
          "bg-white hover:bg-purple-50",
          "shadow-xs hover:shadow-md",
          "focus:outline-hidden focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:border-purple-500",
          "text-gray-700 hover:text-purple-700",
          isOpen && "border-purple-400 bg-purple-50 shadow-md",
          // Largura padronizada com o ContextSwitcher
          variant === "mobile" ? "w-52 sm:w-56" : "w-56 sm:w-64",
        )}
        aria-label={t("Menu do usuário")}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="flex items-center gap-2 sm:gap-2 md:gap-2.5 min-w-0 flex-1">
          {/* Avatar com badge de status */}
          <div className="relative shrink-0">
            <div
              className={cn(
                "flex items-center justify-center",
                "rounded-full bg-linear-to-br from-purple-100 via-purple-200 to-purple-300",
                "text-purple-800 font-semibold",
                "ring-2 ring-purple-200 group-hover:ring-purple-300 transition-all",
                "border-2 border-purple-300",
                "shadow-xs group-hover:shadow-md",
                variant === "mobile"
                  ? "h-5 w-5 sm:h-6 sm:w-6 text-xs sm:text-sm"
                  : "h-6 w-6 sm:h-6 sm:w-6 md:h-7 md:w-7 text-sm sm:text-sm",
              )}
            >
              {userInitials}
            </div>
            {/* Badge de status ativo */}
            <div
              className={cn(
                "absolute -bottom-0.5 -right-0.5",
                "bg-green-500 rounded-full border-2 border-white",
                "flex items-center justify-center",
                "shadow-xs",
                variant === "mobile"
                  ? "w-3 h-3 sm:w-3.5 sm:h-3.5"
                  : "w-3.5 h-3.5 sm:w-4 sm:h-4",
              )}
            >
              <CheckCircle
                className={cn(
                  "text-white",
                  variant === "mobile"
                    ? "h-2 w-2 sm:h-2.5 sm:w-2.5"
                    : "h-2.5 w-2.5 sm:h-3 sm:w-3",
                )}
              />
            </div>
          </div>

          {/* Nome do usuário (oculto em mobile muito pequeno) */}
          <div className="hidden sm:flex flex-col items-start min-w-0">
            <span
              className={cn(
                "font-medium text-gray-900 group-hover:text-purple-700 truncate",
                "transition-colors duration-200",
                variant === "mobile"
                  ? "text-xs sm:text-sm"
                  : "text-sm sm:text-sm",
              )}
            >
              {user.name || user.email}
            </span>
          </div>
        </div>

        {/* Ícone de dropdown - extremidade direita */}
        <ChevronDown
          className={cn(
            "text-gray-500 transition-all duration-200 shrink-0 ml-auto",
            "group-hover:text-purple-600",
            isOpen && "transform rotate-180 text-purple-600",
            variant === "mobile" ? "h-4 w-4" : "h-4 w-4 sm:h-4 sm:w-4",
          )}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          ref={menuRef}
          className={cn(
            "absolute right-0 mt-3 w-64 sm:w-72",
            "bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl shadow-purple-500/10 border border-purple-200",
            "py-2 z-50 overflow-hidden",
            "animate-in fade-in zoom-in-95 duration-200 origin-top-right",
            variant === "mobile" && "right-0",
          )}
          role="menu"
          aria-orientation="vertical"
        >
          {/* Header do menu com informações do usuário */}
          <div className="px-5 py-4 bg-linear-to-br from-purple-50/50 to-white border-b border-purple-50">
            <div className="flex items-center gap-4">
              <div className="relative shrink-0">
                <div
                  className={cn(
                    "flex items-center justify-center rounded-2xl",
                    "bg-purple-600 text-white font-black shadow-lg shadow-purple-600/20",
                    "h-11 w-11 text-base",
                  )}
                >
                  {userInitials}
                </div>
                {/* Badge de status no dropdown */}
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white shadow-xs flex items-center justify-center">
                  <div className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-black uppercase tracking-widest text-purple-400 mb-0.5">
                  {t("Sessão Ativa")}
                </p>
                <p className="text-sm font-black text-gray-900 truncate leading-tight">
                  {user.name || t("Usuário")}
                </p>
                <p className="text-[10px] font-bold text-gray-400 truncate mt-0.5">
                  {user.email}
                </p>
              </div>
            </div>
          </div>

          {/* Itens do menu */}
          <div className="py-2 px-1">
            <Link
              to="/profile"
              onClick={() => setIsOpen(false)}
              onContextMenu={(e) => {
                e.preventDefault();
                setIsOpen(false);
                navigate("/profile");
              }}
              className={cn(
                "flex items-center gap-3 px-5 py-3 rounded-xl transition-all",
                "text-sm font-black text-gray-700",
                "hover:bg-purple-50 hover:text-purple-700",
                "focus:outline-hidden focus:bg-purple-50",
              )}
              role="menuitem"
            >
              <UserCircle className="h-5 w-5 text-purple-500 shrink-0" />
              <span>{t("Meu Perfil")}</span>
            </Link>

            <button
              onClick={handleSettingsClick}
              onContextMenu={(e) => {
                e.preventDefault();
                handleSettingsClick();
              }}
              className={cn(
                "w-full flex items-center gap-3 px-5 py-3 rounded-xl transition-all",
                "text-sm font-black text-gray-700",
                "hover:bg-purple-50 hover:text-purple-700",
                "focus:outline-hidden focus:bg-purple-50 text-left",
              )}
              role="menuitem"
            >
              <Coins className="h-5 w-5 text-purple-500 shrink-0" />
              <span>{t("Créditos e consumo")}</span>
            </button>

            <div className="border-t border-purple-50 my-1.5 mx-2" />
            <p className="px-5 pt-2 pb-1 text-[10px] font-black uppercase tracking-widest text-purple-400">
              {t("Organização")}
            </p>

            <Link
              to="/organization"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-4 px-5 py-3 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all rounded-xl"
              role="menuitem"
            >
              <Building className="h-5 w-5 text-purple-500" />
              <span>{t("Minha Organização")}</span>
            </Link>
            <Link
              to="/organization/clients"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-4 px-5 py-3 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all rounded-xl"
              role="menuitem"
            >
              <Users className="h-5 w-5 text-purple-500" />
              <span>{t("Empresas Cliente")}</span>
            </Link>
            {(user?.org_role === "SUPER_ADMIN" ||
              user?.org_role === "ADMIN") && (
              <Link
                to="/organization/users"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-4 px-5 py-3 text-sm font-black text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-all rounded-xl"
                role="menuitem"
              >
                <UserCog className="h-5 w-5 text-purple-500" />
                <span>{t("Usuários")}</span>
              </Link>
            )}

            {user.is_admin && (
              <button
                onClick={handleAdminCompaniesClick}
                className={cn(
                  "w-full flex items-center gap-3 px-5 py-3 rounded-xl transition-all",
                  "text-sm font-black text-gray-700",
                  "hover:bg-purple-50 hover:text-purple-700",
                  "focus:outline-hidden focus:bg-purple-50 text-left",
                )}
                role="menuitem"
              >
                <Building className="h-5 w-5 text-purple-500 shrink-0" />
                <span>{t("Organizações Globais")}</span>
              </button>
            )}
            {user.is_admin && (
              <button
                onClick={handleAdminPanelClick}
                className={cn(
                  "w-full flex items-center gap-3 px-5 py-3 rounded-xl transition-all",
                  "text-sm font-black text-gray-700",
                  "hover:bg-purple-50 hover:text-purple-700",
                  "focus:outline-hidden focus:bg-purple-50 text-left",
                )}
                role="menuitem"
              >
                <Crown className="h-5 w-5 text-purple-500 shrink-0" />
                <span>{t("Painel Admin (Auditoria)")}</span>
              </button>
            )}
          </div>

          {/* Divisor */}
          <div className="border-t border-purple-50 mx-2 my-1"></div>

          {/* Logout */}
          <div className="py-1 px-1">
            <button
              onClick={handleLogout}
              className={cn(
                "w-full flex items-center gap-3 px-5 py-3 rounded-xl transition-all",
                "text-sm font-black text-rose-600",
                "hover:bg-rose-50",
                "focus:outline-hidden focus:bg-red-50 text-left",
              )}
              role="menuitem"
            >
              <LogOut className="h-5 w-5 shrink-0" />
              <span>{t("Encerrar Sessão")}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileMenu;
